import { usePage } from "@inertiajs/react"
import type { ReactNode } from "react"
import { useEffect, useRef } from "react"

import { ColorSchemeProvider } from "@/hooks/use-color-scheme"
import { WechatShareBridge } from "@/components/share/WechatShareBridge"
import {
  AnchoredToastProvider,
  ToastProvider,
  toastManager,
} from "@/components/ui/toast"

type Flash = {
  notice?: string | null
  alert?: string | null
}

function FlashFromServer() {
  const { url, version, props } = usePage<{ flash?: Flash }>()
  const flash = props.flash
  const lastKey = useRef<string | null>(null)

  useEffect(() => {
    const notice = flash?.notice?.trim() ?? ""
    const alert = flash?.alert?.trim() ?? ""
    if (!notice && !alert) {
      lastKey.current = null
      return
    }

    // 同一 URL 上若文案不变（如多次「已发布」），没有 version 差分会误杀后续提示
    const key = `${String(version)}::${url}::${notice}::${alert}`
    if (lastKey.current === key) {
      return
    }
    lastKey.current = key

    if (notice) {
      toastManager.add({
        title: notice,
        type: "success",
        timeout: 4500,
        priority: "low",
      })
    }
    if (alert) {
      toastManager.add({
        title: alert,
        type: "error",
        timeout: 6000,
        priority: "high",
      })
    }
  }, [version, url, flash?.notice, flash?.alert])

  return null
}

type Props = { children: ReactNode }

export default function InertiaRootLayout({ children }: Props) {
  return (
    <ColorSchemeProvider>
      <ToastProvider position="top-center">
        <AnchoredToastProvider>
          <FlashFromServer />
          <WechatShareBridge />
          {children}
        </AnchoredToastProvider>
      </ToastProvider>
    </ColorSchemeProvider>
  )
}
